import { useEffect, useRef } from 'react';

import { isQA, isSandbox, isProd } from '@src/constants';

import { TestPayerParams, useQueryParams } from '../query-params-hook';

type generateCodePropsType = {
	handle: string;
	wgc: string;
	beaconUrl: string;
	scriptSourceUrl: string;
	testPayer?: TestPayerParams;
};


const safeJsonStringify = (value: string): string =>
	JSON.stringify(value).replace(/</g, '\\u003c').replace(/>/g, '\\u003e');


const escapeInlineScriptString = (value: string): string =>
	value
		.replace(/\\/g, '\\\\')
		.replace(/'/g, "\\'")
		.replace(/</g, '\\u003c')
		.replace(/>/g, '\\u003e')
		.replace(/\r?\n/g, '\\n');

const buildTestPayerLiteral = (testPayer?: TestPayerParams) => {
	if (!testPayer) {
		return '';
	}
	const entries = Object.entries(testPayer).filter(([, value]) => value !== '');
	if (entries.length === 0) {
		return '';
	}
	const fields = entries.map(([key, value]) => `${key}: ${safeJsonStringify(value)}`).join(', ');
	return `testPayer: { ${fields} },`;
};

const generateCode = ({ handle, wgc, beaconUrl, scriptSourceUrl, testPayer }: generateCodePropsType) =>
	`!(function () {
			window.pushpayEmbeddedConfig = {
				handle: '${escapeInlineScriptString(handle)}',
				wgc: '${escapeInlineScriptString(wgc)}',
				${buildTestPayerLiteral(testPayer)}
			};

			setTimeout(handlePushpayEmbeddedLoadError, 3000);

			function handlePushpayEmbeddedLoadError() {
				if (!window.pushpayEmbeddedFallbackDone) {
					window.pushpayEmbeddedFallbackDone = true;
					var beacon = document.createElement('img');
					beacon.src = '${beaconUrl}?error=EmbeddedWidgetLoadFailed';
					beacon.style.cssText = 'height:1px;width:1px;position:absolute;top:0;left:0;z-index:-1';
					document.body.appendChild(beacon);
				}
			}

			var script = document.createElement('script');
			script.type = 'text/javascript';
			script.async = true;
			script.src = '${scriptSourceUrl}';
			script.onerror = handlePushpayEmbeddedLoadError;
			script.onload = function () {
				window.pushpayEmbeddedFallbackDone = true;
			};
			document.getElementsByTagName('head')[0].appendChild(script);
		})();`;

export function DynamicScript() {
	const { handle, wgc, isWidget, testPayer } = useQueryParams();
	const addedRef = useRef(false);

	useEffect(() => {
		if (document.getElementById('pp-form-script') || addedRef.current) {
			return;
		}

		const script = document.createElement('script');
		script.id = 'pp-dynamic-script';

		if (isQA()) {
			script.innerHTML = generateCode({
				handle: handle || 'embeddynamic',
				wgc:
					wgc ||
					'eyJyYnUiOiJodHRwczovL2VtYmVkZGVkLWdpdmluZy1kZW1vLXBhZ2UudGVzdHB1c2hwYXkuY29tLyIsInJidCI6IkJhY2sgdG8gc2l0ZSIsImFza2dwIjp0cnVlfTozTEs1cW1BMGwyZ19fSkFveTVSX1VBN25La2M',
				beaconUrl: 'https://qa.testpushpay.com/Content/Beacons/eb.gif',
				scriptSourceUrl: 'https://qa-embedded.testpushpay.com',
				testPayer,
			});
		}

		if (isSandbox()) {
			script.innerHTML = generateCode({
				handle: handle || 'embcentralchurch',
				wgc:
					wgc ||
					'eyJyYnUiOiJodHRwczovL2VtYmVkZGVkLWdpdmluZy5jY2h1cmNoLmNvLyIsInJidCI6IkJhY2sgdG8gc2l0ZSIsImFza2dwIjp0cnVlfTpXcDNWcERMczhFbU5DdkhHNWVoZUFySGtVM28',
				beaconUrl: 'https://sandbox.pushpay.io/Content/Beacons/eb.gif',
				scriptSourceUrl: 'https://embedded.pushpay.io',
			});
		}

		if (isProd()) {
			script.innerHTML = generateCode({
				handle: handle || 'pushpay-cs01',
				wgc:
					wgc ||
					'eyJyYnUiOiJodHRwczovL25hc2h2aWxsZW5vcnRoLmNjLyIsInJidCI6IlJldHVybiB0byBTaXRlIiwiYXNrZ3AiOmZhbHNlfTpwd2l4TDdzWTlPSUw0eGNNVy1NM1FvdWNnOVU',
				beaconUrl: 'https://pushpay.com/Content/Beacons/eb.gif',
				scriptSourceUrl: 'https://embedded.pushpay.com',
			});
		}

		document.body.appendChild(script);
		addedRef.current = true;
		// eslint-disable-next-line consistent-return
		return () => {
			document.body.removeChild(script);
			addedRef.current = false;
		};
	}, [handle, wgc, isWidget, testPayer]);

	return null;
}
